import { FC } from 'react';
import {
  BorderLinearProgress,
  DashboardContainer,
  ContainerContent,
  DashboardContainerHeader,
  DashboardContainerBody,
  ContainerScroll,
  ContainerSubText,
  ContainersWrapper,
  DividerContainer,
  H2,
  H3,
  ResolvedText,
  StyledTitle,
} from '../MoaDashboard.styles';
import { ActivationsContainerProps } from './ActivationsContainer.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DashboardMetricsDetails } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams } from 'ag-grid-community';

const ActivationsContainer: FC<ActivationsContainerProps> = ({
  inProgress,
  upNext,
  done,
  setDrawerColumnDefs,
  setOpenDrawer,
  setDrawerType,
  setFetchExtractionsFailure,
  setExtractionAndActivationsJobId,
  setRequestUserName,
}) => {
  const muiTheme = createTheme();
  const activationsColumnDefs = [
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'label',
      headerName: 'Store#',
      sortable: true,
      floatingFilter: false,
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'marketNo',
      headerName: 'FZ Code',
      sortable: false,
      floatingFilter: false,
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'isResolved',
      headerName: '',
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return <ResolvedText>Resolved</ResolvedText>;
        } else {
          return '';
        }
      },
    },
  ];
  const defineDrawer = (progressMetrics: DashboardMetricsDetails) => {
    setDrawerColumnDefs(activationsColumnDefs);
    setFetchExtractionsFailure(true);
    setOpenDrawer(true);
    setDrawerType('activate');
    setExtractionAndActivationsJobId(progressMetrics.jobId);
    setRequestUserName(progressMetrics.userName);
  };

  const renderStatus = (progressMetrics: DashboardMetricsDetails) => {
    const resolved = Number(progressMetrics.resolved);
    if (progressMetrics.failed === 0 || progressMetrics.failed === null) {
      return null;
    }

    if (progressMetrics.failed > resolved) {
      return (
        <>
          <ErrorOutlineIcon
            data-testid={'failedIcon-' + progressMetrics.jobId}
            fontSize='medium'
            aria-label='Incomplete'
            style={{
              color: 'red',
              marginLeft: 'auto',
              alignContent: 'flex-end',
              paddingRight: '5px',
              paddingTop: '3px',
            }}
          />
          <H3
            dataTestId={'failedLink-' + progressMetrics.jobId}
            label={progressMetrics.failed - resolved + ' failed'}
            to={''}
            style={{ paddingLeft: '0' }}
            onClick={() => defineDrawer(progressMetrics)}
          />
        </>
      );
    }

    return (
      <>
        <H3
          dataTestId={'resolvedText-' + progressMetrics.jobId}
          label={progressMetrics.resolved + ' resolved'}
          to={''}
          style={{ paddingLeft: '0' }}
          onClick={() => defineDrawer(progressMetrics)}
        />
      </>
    );
  };

  const renderInProgress = () => {
    if (inProgress.length === 0) {
      return (
        <ContainerScroll data-testid='activateEmptyContainer' style={{ height: '160px', alignItems: 'baseline' }}>
          <StyledTitle>No activations in progress</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='activateInProgressContainer' style={{ height: '160px' }}>
          {inProgress.map((inProgressMetrics, index: number) => {
            return (
              <div key={'activation-progress-' + index}>
                <StyledTitle>Processing Menus for {inProgressMetrics.userName}</StyledTitle>
                <ContainerContent style={{ position: 'relative' }}>
                  <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
                    <H2
                      style={{
                        paddingTop: '4px',
                        paddingLeft: '5px',
                        position: 'absolute',
                        left: '15px',
                        zIndex: 2,
                        fontWeight: 600,
                      }}
                    >
                      {inProgressMetrics.processed}
                    </H2>
                    <BorderLinearProgress
                      variant='determinate'
                      data-testid={'activateProgressBar-' + inProgressMetrics.jobId}
                      value={
                        inProgressMetrics.processed ? (inProgressMetrics.processed / inProgressMetrics.total) * 100 : 0
                      }
                      style={{ width: '60%' }}
                    />
                  </ThemeProvider>
                  <H2
                    style={{
                      paddingTop: '4px',
                      paddingLeft: '5px',
                      fontWeight: 600,
                    }}
                  >
                    {inProgressMetrics.total}
                  </H2>
                  {renderStatus(inProgressMetrics)}
                </ContainerContent>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  const renderUpNext = () => {
    if (upNext.length === 0) {
      return (
        <ContainerScroll style={{ height: '110px', alignItems: 'baseline' }}>
          <StyledTitle>No activations waiting</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='upNextActivateContainer' style={{ height: '110px' }}>
          {upNext.map((upNextMetrics, index: number) => {
            return (
              <div
                data-testid={'upNextActivate-' + upNextMetrics.jobId + '-row'}
                style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between' }}
                key={'activation-next-' + index}
              >
                <ContainersWrapper data-testid={'upNextActivate-' + upNextMetrics.jobId + '-total'}>
                  <H2
                    style={{
                      justifyContent: 'center',
                      alignItems: 'end',
                      paddingTop: '10px',
                      paddingLeft: '20px',
                      width: '10px',
                      fontWeight: 600,
                    }}
                  >
                    {upNextMetrics.total}
                  </H2>
                </ContainersWrapper>
                <div
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'end',
                    flexDirection: 'row',
                    display: 'flex',
                    flexWrap: 'wrap',
                    overflow: 'hidden',
                    width: '70%',
                  }}
                >
                  <ContainerSubText></ContainerSubText>
                  <ContainerSubText
                    data-testid={'upNextActivate-' + upNextMetrics.jobId + '-user'}
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >
                    {upNextMetrics.userName}
                  </ContainerSubText>
                </div>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  const renderDone = () => {
    if (done.length === 0) {
      return (
        <ContainerScroll style={{ height: '150px', alignItems: 'baseline' }}>
          <StyledTitle>No activations</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='doneActivateContainer' style={{ height: '150px' }}>
          {done.map((doneMetrics, index: number) => {
            return (
              <div
                data-testid={'doneActivate-' + doneMetrics.jobId + '-row'}
                style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between' }}
                key={'activation-done-' + index}
              >
                <ContainersWrapper>
                  <H2
                    data-testid={'doneActivate-' + doneMetrics.jobId + '-total'}
                    style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: '10px',
                      paddingLeft: '20px',
                      width: '40px',
                      fontWeight: 600,
                    }}
                  >
                    {doneMetrics.total}
                  </H2>
                  {renderStatus(doneMetrics)}
                </ContainersWrapper>
                <div
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    width: '70%',
                  }}
                >
                  <ContainerSubText></ContainerSubText>
                  <ContainerSubText
                    data-testid={'doneActivate-' + doneMetrics.jobId + '-user'}
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >
                    {doneMetrics.userName}
                  </ContainerSubText>
                </div>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  return (
    <DashboardContainer data-testid={'activationsContainer'} style={{ minHeight: '400px', maxHeight: '600px' }}>
      <DashboardContainerHeader data-testid='activationsContainerHeader'>Activations</DashboardContainerHeader>
      <DashboardContainerBody>
        {renderInProgress()}
        <DividerContainer>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
        </DividerContainer>
        <StyledTitle>Up next</StyledTitle>
        {renderUpNext()}
        <DividerContainer>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
        </DividerContainer>
        <StyledTitle>Done</StyledTitle>
        {renderDone()}
      </DashboardContainerBody>
    </DashboardContainer>
  );
};

export default ActivationsContainer;
